import moment from 'moment';

import {
    memoizeDeafultSerialize,
    memoizeOnClient,
} from 'wanderu-utils';

const MULTIMODAL_TYPE = 'multi';
const MEMOIZE_LIMIT = 750;
function _memoizeSerializer(trip, ...otherArgs) {
    return memoizeDeafultSerialize(trip.tripId, ...otherArgs);
}

function _filterByLocationMatch(tripMetadata = {}, allowNearby = false) {
    if (allowNearby) {
        return true;
    }

    const {
        exactOriginMatch,
        exactDestMatch,
    } = tripMetadata;
    const isNearby = !exactOriginMatch || !exactDestMatch;

    return !isNearby;
}

function _filterByTransfers(trip, directOnly) {
    if (!directOnly) {
        return true;
    }

    return trip.transfers === 0;
}

function _filterByCarriers(trip, carriers) {
    return _getCarriersForTrip(trip).some((carrierCode) => {
        return _getModesForTrip(trip).some((mode) => {
            return carriers[carrierCode] && carriers[carrierCode][mode];
        });
    });
}

const _memoFilterByCarriers = memoizeOnClient(
    _filterByCarriers,
    {
        limit: MEMOIZE_LIMIT,
        serializer: _memoizeSerializer,
    },
);

function _getModesForTrip(trip) {
    let { tripModes: travelModes } = trip;

    const isMultiModal = new Set([...travelModes]).size > 1;
    if (isMultiModal) {
        return [MULTIMODAL_TYPE];
    }

    return travelModes;
}

function _getCarriersForTrip(trip) {
    if (trip.isRouted) {
        return trip.tripLegs.filtered.map((leg) => leg.carrierCode);
    }

    return [trip.carrierCode];
}

function _filterByFeatures(trip, features) {
    return Object.entries(features).every(([featureId, isActive]) => {
        if (!isActive) {
            return true;
        }

        return tripHasFeature(trip, featureId);
    });
}

function _filterByStations(trip, stations = { arrival: {}, departure: {} }) {
    const {
        arrival = {},
        departure = {},
    } = stations;
    const tripHasArrivalStation = Object.entries(arrival).some(([stationId, isActive]) => {
        return isActive && trip.arriveId === stationId;
    });
    const tripHasDepartureStation = Object.entries(departure).some(([stationId, isActive]) => {
        return isActive && trip.departId === stationId;
    });

    return tripHasArrivalStation && tripHasDepartureStation;
}

function tripHasFeature(trip, featureId) {
    if (trip.isRouted) {
        return trip.tripLegs.filtered
            .some((leg) => leg.features.includes(featureId));
    }

    return trip.features.includes(featureId);
}

function _filterByTime(trip, timingObj) {
    return Object.entries(timingObj)
        .every(([key, range]) => {
            let date;
            if (key === 'arrival') {
                date = moment.parseZone(trip.localizedArriveDateTime);
            } else {
                date = moment.parseZone(trip.localizedDepartDateTime);
            }

            const tripHour = date.hours();
            const [min, max] = range;
            return min <= tripHour && tripHour < max;
        });
}

const _memoFilterByTime = memoizeOnClient(
    _filterByTime,
    {
        limit: MEMOIZE_LIMIT,
        serializer: _memoizeSerializer,
    },
);

function _filterByDepartTime(trip, leavesAfter) {
    const departDate = moment.parseZone(trip.localizedDepartDateTime);
    if (departDate.isBefore(moment())) {
        return false;
    }

    if (!leavesAfter) {
        return true;
    }

    return departDate.isAfter(moment.parseZone(leavesAfter));
}

function filterTrips(filterData, leavesAfter, trips, tripMetadata) {
    if (!trips) {
        return [];
    }

    return trips.filter((trip) => {
        const metaData = tripMetadata[trip.tripId];
        return tripSatisifiesFilters(filterData, leavesAfter, trip, metaData);
    });
}

function tripSatisifiesFilters(filterData, leavesAfter, trip, tripMetadata) {
    if (!trip) {
        return false;
    }

    const validLocationMatch = _filterByLocationMatch(tripMetadata, filterData.allowNearby);
    if (!validLocationMatch) {
        return false;
    }

    return _tripSatisifiesFiltersWithNearby(filterData, leavesAfter, trip);
}

function _tripSatisifiesFiltersWithNearby(filterData, leavesAfter, trip) {
    const validDepartTime = _filterByDepartTime(trip, leavesAfter);
    if (!validDepartTime) {
        return false;
    }

    const validTransfers = _filterByTransfers(trip, filterData.directOnly);
    if (!validTransfers) {
        return false;
    }

    const validCarrier = _memoFilterByCarriers(trip, filterData.carriers);
    if (!validCarrier) {
        return false;
    }

    const validFeatures = _filterByFeatures(trip, filterData.features);
    if (!validFeatures) {
        return false;
    }

    const validStations = _filterByStations(trip, filterData.stations);
    if (!validStations) {
        return false;
    }

    const validTime = _memoFilterByTime(trip, filterData.timing);
    if (!validTime) {
        return false;
    }

    return true;
}

function filterTripsWithNearby(filterData, leavesAfter, trips) {
    if (!trips) {
        return [];
    }

    return trips.filter((trip) => _tripSatisifiesFiltersWithNearby(filterData, leavesAfter, trip));
}

function filterNearby(filterData, trips, tripMetadata) {
    return trips.filter((trip) => {
        const metadata = tripMetadata[trip.tripId];
        return _filterByLocationMatch(metadata, filterData.allowNearby);
    });
}

function filterTripsByRangeAndMode(trips, range, mode) {
    return trips.filter((trip) => {
        // NB: Multi-modal trips are included if it includes the filtered mode
        const includesSelectedMode = mode && trip.tripModes.includes(mode);
        const isWithinRange = _filterByTime(trip, range);

        return isWithinRange && includesSelectedMode;
    });
}

export {
    filterNearby,
    filterTripsByRangeAndMode,
    filterTripsWithNearby,
    tripSatisifiesFilters,
};
export default filterTrips;
